import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import Button from '@/components/Button'
import DeleteConfirmationOverlay from '@/components/DeleteConfirmationOverlay'
import ErrorConfirmation from '@/components/ErrorConfirmation'
import { Plus } from '@/components/Icons'
import Page from '@/components/Page'
import { resetRequest } from '@/store/actions'
import { isRequestError, isRequestPending } from '@/store/selectors'

import { deleteAttribute, fetchAttributes } from './actions'
import { DELETE_ATTRIBUTE, FETCH_ATTRIBUTES } from './actionTypes'
import AttributeList from './AttributeList'
import attributeShape from './attributeShape'
import messages from './messages'
import { getAttributesInOrder } from './selectors'

function OrganizationUserAttributesPage(props: any) {
  const intl = useIntl()
  const history = useHistory()
  const location = useLocation()

  const [state, setState] = useState({
    attributeIdToDelete: false
  })

  // componentDidMount
  useEffect(() => {
    props.actions.resetRequest([DELETE_ATTRIBUTE, FETCH_ATTRIBUTES])
    props.actions.fetchAttributes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const navigateToCreateAttribute = () => {
    history.push(`${location.pathname}/create`)
  }

  const navigateToEditAttribute = (attributeId: any) => {
    history.push(`${location.pathname}/${attributeId}/edit`)
  }

  const onDeleteAttribute = async () => {
    if (state.attributeIdToDelete) {
      await props.actions.deleteAttribute(state.attributeIdToDelete)
      hideDeleteConfirmation()
    }
  }

  const showDeleteConfirmation = (attributeId: any) => {
    setState({ attributeIdToDelete: attributeId })
  }

  const hideDeleteConfirmation = () => {
    setState({ attributeIdToDelete: false })
  }

  const goBack = () => {
    history.push(location.pathname.slice(0, Math.max(0, location.pathname.lastIndexOf('/'))))
  }

  return (
    <Page isLoading={props.isLoading} loadingMessage={<FormattedMessage {...messages.loading} />}>
      <Page.Header
        buttons={
          <Button color="primary" className="button--primary float-right" onClick={navigateToCreateAttribute}>
            <>
              <Plus />
              {intl.formatMessage(messages.createButton)}
            </>
          </Button>
        }
        title={<FormattedMessage {...messages.title} />}
        goBack={goBack}
        backNavTitle={<FormattedMessage {...messages.back} />}
      />

      {props.hasError ? (
        <ErrorConfirmation
          error={intl.formatMessage(messages.errorTitle)}
          subMessage={intl.formatMessage(messages.errorMessage)}
        />
      ) : (
        <>
          <AttributeList
            attributes={props.attributes}
            onDelete={showDeleteConfirmation}
            onEdit={navigateToEditAttribute}
          />

          {!!state.attributeIdToDelete && (
            <DeleteConfirmationOverlay
              onCancel={hideDeleteConfirmation}
              onConfirm={onDeleteAttribute}
              message={<FormattedMessage {...messages.confirmDeleteTitle} />}
              subMessage={<FormattedMessage {...messages.confirmDeleteMessage} />}
            />
          )}
        </>
      )}
    </Page>
  )
}

OrganizationUserAttributesPage.displayName = 'OrganizationUserAttributesPage'

OrganizationUserAttributesPage.propTypes = {
  actions: PropTypes.shape({
    deleteAttribute: PropTypes.func.isRequired,
    fetchAttributes: PropTypes.func.isRequired,
    resetRequest: PropTypes.func.isRequired
  }).isRequired,
  attributes: PropTypes.arrayOf(attributeShape),
  hasError: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired
}

OrganizationUserAttributesPage.defaultProps = {
  attributes: [],
  hasError: false
}

const DisconnectedOrganizationUserAttributesPage = OrganizationUserAttributesPage
export { /** @deprecated */ DisconnectedOrganizationUserAttributesPage as OrganizationUserAttributesPage }

const mapStateToProps = (state: any) => ({
  attributes: getAttributesInOrder(state),
  hasError: isRequestError(state, [DELETE_ATTRIBUTE, FETCH_ATTRIBUTES]),
  isLoading: isRequestPending(state, [DELETE_ATTRIBUTE, FETCH_ATTRIBUTES])
})

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators({ deleteAttribute, fetchAttributes, resetRequest }, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(DisconnectedOrganizationUserAttributesPage)
